import type { VideoPresentation, VideoPresentationTarget } from '@playcastdotio/PeerCore';

export type VideoPresentationMode = 'stream' | 'webcodecs';

export type CanvasPresentationTargetOptions = {
  /** The conventional `<video>`. The canvas mirrors its layout and sits directly above it. */
  video: HTMLVideoElement;
  /** Fires on every transition between conventional and WebCodecs presentation. */
  onModeChange?: (mode: VideoPresentationMode) => void;
  /** Longest the last canvas image is retained after fallback before the `<video>` is trusted regardless. */
  handbackTimeoutMs?: number;
};

/** Layout attributes copied from the `<video>` so the canvas occupies exactly the same box. */
const MIRRORED_ATTRIBUTES = ['class', 'style', 'hidden', 'data-force-landscape'];
/** The video is `z-10`; the canvas paints just above it and lets pointer events fall through. */
const CANVAS_Z_INDEX = '11';
const DEFAULT_HANDBACK_TIMEOUT_MS = 2_000;

type RvfcVideo = HTMLVideoElement & { requestVideoFrameCallback?: (cb: () => void) => number };

/**
 * The player's WebCodecs surface. Owns one `<canvas>` placed as the `<video>`'s sibling and
 * mirrors the video's class/style/orientation attributes, so every existing sizing,
 * rotation and fullscreen rule — and the input mapping that reads the video's box — applies
 * unchanged. The canvas is only shown while WebCodecs has drawn; on fallback it stays up
 * until the `<video>` presents a fresh frame, so the viewer never sees a gap.
 */
export class CanvasPresentationTarget implements VideoPresentationTarget {
  private canvas: HTMLCanvasElement | null = null;
  private observer: MutationObserver | null = null;
  private mode: VideoPresentationMode = 'stream';
  private cancelHandback: (() => void) | null = null;

  constructor(private readonly options: CanvasPresentationTargetOptions) {}

  getMode(): VideoPresentationMode { return this.mode; }

  acquireCanvas(): HTMLCanvasElement {
    // A transferred canvas can never be re-transferred; a fresh element per session is the contract.
    // Replacing one WebCodecs canvas keeps that mode, so the Electron <video> stays covered.
    this.releaseCanvas(true);
    const canvas = document.createElement('canvas');
    canvas.dataset['testid'] = 'webcodecs-surface';
    this.canvas = canvas;
    this.mirrorLayout();
    this.observer = new MutationObserver(() => this.mirrorLayout());
    this.observer.observe(this.options.video, { attributes: true, attributeFilter: MIRRORED_ATTRIBUTES });
    this.options.video.insertAdjacentElement('afterend', canvas);
    return canvas;
  }

  present(presentation: VideoPresentation): void {
    if (presentation.kind === 'webcodecs') {
      this.stopHandback();
      this.setMode('webcodecs');
      this.mirrorLayout();
      return;
    }
    if (this.mode === 'webcodecs') this.beginHandback();
    this.setMode('stream');
  }

  releaseCanvas(retainMode = false): void {
    this.stopHandback();
    this.observer?.disconnect();
    this.observer = null;
    this.canvas?.remove();
    this.canvas = null;
    if (!retainMode) this.setMode('stream');
  }

  private setMode(mode: VideoPresentationMode): void {
    if (this.mode === mode) return;
    this.mode = mode;
    this.mirrorLayout();
    this.options.onModeChange?.(mode);
  }

  /** Copy the video's layout attributes, then re-apply the overrides that make the canvas a canvas. */
  private mirrorLayout(): void {
    const canvas = this.canvas;
    if (!canvas) return;
    const video = this.options.video;
    for (const name of MIRRORED_ATTRIBUTES) {
      const value = video.getAttribute(name);
      if (value === null) canvas.removeAttribute(name);
      else canvas.setAttribute(name, value);
    }
    canvas.style.zIndex = CANVAS_Z_INDEX;
    canvas.style.pointerEvents = 'none';
    const showing = this.mode === 'webcodecs' || this.cancelHandback !== null;
    if (!showing) canvas.style.display = 'none';
  }

  /** Keep the last drawn image until the `<video>` paints a new frame (or the timeout elapses). */
  private beginHandback(): void {
    this.stopHandback();
    const video = this.options.video as RvfcVideo;
    const done = () => { this.stopHandback(); this.mirrorLayout(); };
    const timer = setTimeout(done, this.options.handbackTimeoutMs ?? DEFAULT_HANDBACK_TIMEOUT_MS);
    let settled = false;
    const once = () => { if (!settled) { settled = true; done(); } };
    if (typeof video.requestVideoFrameCallback === 'function') video.requestVideoFrameCallback(once);
    else video.addEventListener('timeupdate', once);
    this.cancelHandback = () => {
      settled = true;
      clearTimeout(timer);
      video.removeEventListener('timeupdate', once);
    };
  }

  private stopHandback(): void {
    this.cancelHandback?.();
    this.cancelHandback = null;
  }
}
